import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import {
  getDailyPrayers,
  deleteDailyPrayer,
  createDailyPrayer,
  updateDailyPrayer,
} from "../../../utils/api";

const prayerTypes = [
  "Early Morning",
  "Morning",
  "Afternoon",
  "Late Afternoon",
  "Evening",
];

const columns = [
  { key: "title", label: "Title" },
  { key: "type", label: "Type" },
  { key: "category", label: "Category" },
  {
    key: "duration",
    label: "Duration (min)",
    render: (value) => (Number.isFinite(Number(value)) ? Number(value) : "-"),
  },
  {
    key: "timeRange",
    label: "Time Range",
    render: (value) => {
      if (!value || typeof value !== "object") return "-";
      const startHour = Number(value.startHour);
      const startMinute = Number(value.startMinute);
      const endHour = Number(value.endHour);
      const endMinute = Number(value.endMinute);

      if (
        [startHour, startMinute, endHour, endMinute].some((part) => Number.isNaN(part))
      ) {
        return "-";
      }

      const pad = (num) => String(num).padStart(2, "0");
      return `${pad(startHour)}:${pad(startMinute)} - ${pad(endHour)}:${pad(endMinute)}`;
    },
  },
];

const filters = [
  {
    key: "type",
    label: "Filter by Type",
    type: "select",
    options: prayerTypes.map((type) => ({ value: type, label: type })),
  },
  {
    key: "category",
    label: "Filter by Category",
    type: "search",
    placeholder: "Search category",
  },
];

const initialFormData = {
  title: "",
  description: "",
  transcript: "",
  type: "Morning",
  category: "General",
  duration: "10",
  image: "",
  audioBook: "",
  startHour: "5",
  startMinute: "0",
  endHour: "7",
  endMinute: "0",
};

export default function DailyPrayers() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedPrayer(null);
    setModalOpen(true);
  };

  const handleEdit = (prayer) => {
    const timeRange = prayer.timeRange || {};

    setSelectedPrayer(prayer);
    setFormData({
      title: prayer.title || "",
      description: prayer.description || "",
      transcript: prayer.transcript || "",
      type: prayer.type || "Morning",
      category: prayer.category || "General",
      duration:
        prayer.duration !== undefined && prayer.duration !== null
          ? String(prayer.duration)
          : "10",
      image: prayer.image || "",
      audioBook: prayer.audioBook || "",
      startHour:
        timeRange.startHour !== undefined && timeRange.startHour !== null
          ? String(timeRange.startHour)
          : "5",
      startMinute:
        timeRange.startMinute !== undefined && timeRange.startMinute !== null
          ? String(timeRange.startMinute)
          : "0",
      endHour:
        timeRange.endHour !== undefined && timeRange.endHour !== null
          ? String(timeRange.endHour)
          : "7",
      endMinute:
        timeRange.endMinute !== undefined && timeRange.endMinute !== null
          ? String(timeRange.endMinute)
          : "0",
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (prayer) => {
    handleEdit(prayer);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      title: formData.title,
      description: formData.description,
      transcript: formData.transcript,
      type: formData.type,
      category: formData.category,
      duration: Number(formData.duration),
      image: formData.image,
      audioBook: formData.audioBook,
      timeRange: {
        startHour: Number(formData.startHour),
        startMinute: Number(formData.startMinute),
        endHour: Number(formData.endHour),
        endMinute: Number(formData.endMinute),
      },
    };

    try {
      if (isEditMode && selectedPrayer) {
        await updateDailyPrayer(selectedPrayer._id, payload);
        alert("Daily prayer updated successfully!");
      } else {
        await createDailyPrayer(payload);
        alert("Daily prayer created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save daily prayer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Daily Prayers"
          fetchData={getDailyPrayers}
          deleteData={deleteDailyPrayer}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search daily prayers..."
        />
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Daily Prayer" : "Add New Daily Prayer"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
                placeholder="Enter prayer title"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  required
                >
                  {prayerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Category *</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  required
                  placeholder="General"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Duration (minutes) *</label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleFormChange}
                required
                min="1"
                step="1"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Optional description"
              />
            </div>

            <div className="form-group">
              <label>Transcript</label>
              <textarea
                name="transcript"
                value={formData.transcript}
                onChange={handleFormChange}
                rows="5"
                placeholder="Prayer text"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Time Window</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Start Hour *</label>
                <input
                  type="number"
                  name="startHour"
                  value={formData.startHour}
                  onChange={handleFormChange}
                  required
                  min="0"
                  max="23"
                />
              </div>
              <div className="form-group">
                <label>Start Minute *</label>
                <input
                  type="number"
                  name="startMinute"
                  value={formData.startMinute}
                  onChange={handleFormChange}
                  required
                  min="0"
                  max="59"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>End Hour *</label>
                <input
                  type="number"
                  name="endHour"
                  value={formData.endHour}
                  onChange={handleFormChange}
                  required
                  min="0"
                  max="23"
                />
              </div>
              <div className="form-group">
                <label>End Minute *</label>
                <input
                  type="number"
                  name="endMinute"
                  value={formData.endMinute}
                  onChange={handleFormChange}
                  required
                  min="0"
                  max="59"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Media Links</h3>
            <div className="form-group">
              <label>Audio URL</label>
              <input
                type="text"
                name="audioBook"
                value={formData.audioBook}
                onChange={handleFormChange}
                placeholder="Morning/filename.mp3 or https://..."
              />
            </div>
            <div className="form-group">
              <label>Image URL</label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading
                ? "Saving..."
                : isEditMode
                ? "Update Daily Prayer"
                : "Create Daily Prayer"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
